import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { DrillProvider, useDrill } from 'contexts/DrillContext';
import type { DrillDimension } from 'contexts/DrillContext';
import { useDrillReport } from 'hooks/api/useDrillReport';
import type { DrillQueryRequest, DrillRow } from 'api/types/drillReport.types';
import { SyutFilterSummaryBar, type FilterDisplayItem } from './SyutFilterSummaryBar';
import { SyutDrillBreadcrumb } from './SyutDrillBreadcrumb';
import { SyutDrillSelector } from './SyutDrillSelector';
import { SyutDrillGrid } from './SyutDrillGrid';
import './syutDrill.css';

// Helpers for summary footer
function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return '\u2014';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}
function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || amount === 0) return '\u2014';
  return `\u20B9 ${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export interface SyutDrillScreenProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  rootGroupBy: DrillDimension;
  defaultDrillPath: DrillDimension[];
  availableDimensions: DrillDimension[];
  /** Base query filters from the side filter panel (date range, manager, etc.) */
  baseQueryParams: Record<string, any>;
  /** Human-readable display of base filters for the summary bar */
  baseFilterDisplay: FilterDisplayItem[];
  /** Called when user clicks "Reset" on the filter summary bar */
  onResetBaseFilters?: () => void;
  /** Filter panel JSX to render as sibling (existing side panel) */
  filterPanel?: React.ReactNode;
  filterOpen?: boolean;
  onToggleFilter?: () => void;
  activeFilterCount?: number;
  /**
   * Show a search input in the ModuleHeader. Default: true.
   * The drill wrapper owns the input state, debounces it, and injects the
   * resulting searchTerm into the backend query — the consumer doesn't
   * need to wire anything beyond opting in (or out) and an optional
   * placeholder.
   * Backend already accepts `searchTerm` on DrillQueryRequest; the matching
   * SQL searches employee / engagement / client / activity names depending
   * on the current drill dimension.
   */
  enableSearch?: boolean;
  searchPlaceholder?: string;
}

// Inner component that uses DrillContext (must be inside DrillProvider)
const DrillScreenInner: React.FC<SyutDrillScreenProps> = ({
  title, description, icon, defaultDrillPath,
  baseQueryParams, baseFilterDisplay, onResetBaseFilters,
  filterPanel, filterOpen, onToggleFilter, activeFilterCount,
  enableSearch = true, searchPlaceholder,
}) => {
  const { state, drillFilters } = useDrill();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ----- Search input + debounce -----
  // The user types into the ModuleHeader search field; we mirror the input
  // immediately for snappy UI, then push a debounced value through to the
  // backend query 400ms after they stop typing.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when drill level, base params, or search term changes —
  // otherwise a search applied while on page 5 of the previous result
  // would 404 against the new (smaller) result set.
  const paramsKey = JSON.stringify({
    ...baseQueryParams, ...drillFilters,
    groupBy: state.currentGroupBy, searchTerm: debouncedSearch,
  });
  useEffect(() => { setPage(0); }, [paramsKey]);

  // Build full query from base + drill filters + groupBy + pagination + search.
  // searchTerm is added LAST so it can override any baseQueryParams.searchTerm
  // the consumer might have set (uncommon but defensive).
  const queryParams: DrillQueryRequest = useMemo(() => ({
    ...baseQueryParams,
    ...drillFilters,
    groupBy: state.currentGroupBy as any,
    page: page + 1,
    pageSize,
    ...(debouncedSearch ? { searchTerm: debouncedSearch } : {}),
  }), [baseQueryParams, drillFilters, state.currentGroupBy, page, pageSize, debouncedSearch]);

  const { data: rawData, pagination, summary, loading, error, refetch } = useDrillReport(queryParams);

  // Synthesize id for SyutGrid
  const data = useMemo(() => rawData.map((r, i) => ({
    id: r.groupKey || String(i),
    ...r,
  })), [rawData]);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh',
      onClick: () => refetch(), variant: 'icon' as const,
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={title}
        description={description}
        icon={icon}
        searchConfig={enableSearch ? {
          value: searchInput,
          onChange: setSearchInput,
          placeholder: searchPlaceholder || 'Search...',
        } : undefined}
        filterConfig={onToggleFilter ? {
          onClick: onToggleFilter,
          isActive: filterOpen || false,
          activeFilterCount,
        } : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Base filter summary */}
            <SyutFilterSummaryBar filters={baseFilterDisplay} onReset={onResetBaseFilters} />

            {/* Drill breadcrumb + selector row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              borderLeft: '1px solid var(--border-color)',
              borderRight: '1px solid var(--border-color)',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SyutDrillBreadcrumb />
              </div>
              <div style={{ padding: '0.2rem 0.75rem' }}>
                <SyutDrillSelector />
              </div>
            </div>

            {/* Error display */}
            {error && (
              <div style={{
                padding: '0.5rem 0.75rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderTop: 'none',
                color: '#991b1b',
                fontSize: '0.8rem',
              }}>
                Error loading data: {error.message || 'Check browser console for details'}
              </div>
            )}

            {/* Grid */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <SyutDrillGrid
                data={data}
                pagination={pagination}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                loading={loading}
                defaultDrillPath={defaultDrillPath}
              />
            </div>

            {/* Summary footer */}
            {summary && (
              <div style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '0.4rem 0.9rem',
                background: 'var(--bg-secondary, #f8fafc)',
                border: '1px solid var(--border-color)',
                borderTop: '2px solid var(--border-color)',
                fontSize: '0.8125rem',
                flexWrap: 'wrap',
              }}>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {summary.entryCount} {summary.entryCount === 1 ? 'entry' : 'entries'}
                  {' '}&middot;{' '}
                  {state.currentGroupBy}: {pagination.totalItems} {pagination.totalItems === 1 ? 'group' : 'groups'}
                </span>
                <div style={{ display: 'flex', gap: '1.25rem', fontWeight: 600 }}>
                  <span>Total: {formatMinutes(summary.totalMinutes)}</span>
                  <span>Standard: {formatCurrency(summary.totalStandardAmount)}</span>
                  <span>Discounted: {formatCurrency(summary.totalDiscountedAmount)}</span>
                  <span>Premium: {formatCurrency(summary.totalPremiumAmount)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel (sibling, from report screen) */}
        {filterPanel}
      </div>
    </div>
  );
};

// Outer component wraps with DrillProvider
export const SyutDrillScreen: React.FC<SyutDrillScreenProps> = (props) => (
  <DrillProvider
    rootGroupBy={props.rootGroupBy}
    allDimensions={props.availableDimensions}
    defaultDrillPath={props.defaultDrillPath}
  >
    <DrillScreenInner {...props} />
  </DrillProvider>
);

export default SyutDrillScreen;
